/**
 * What: Shows browser-local unread Shopify study reports as a persistent in-app banner.
 * Transport / data-flow: Consumes shared StudyRun activity state and routes the actionable banner to the loaded report.
 *
 */
import {
  formatShopifyStudyActivityBannerAction,
  formatShopifyStudyActivityBannerBody,
  formatShopifyStudyActivityBannerTitle,
} from '@testfeed/product-language'
import { useShopifyStudyActivity } from '../ShopifyStudyActivityContext'
import { useBannerDismiss } from '../hooks/useBannerDismiss'

/** Renders a compact count until each report has successfully loaded. */
export function ShopifyStudyActivityBanner() {
  const { unreadRunIds, openReport, markReportRead, activityError, refreshStudyActivity } = useShopifyStudyActivity()
  const unreadCount = unreadRunIds.length
  const firstUnreadRunId = unreadRunIds[0] ?? null
  const bannerRef = useBannerDismiss(!!firstUnreadRunId, () => unreadRunIds.forEach((id) => markReportRead(id)))

  if (activityError)
    return (
      <s-banner tone="warning">
        <s-paragraph>{activityError}</s-paragraph>
        <s-button onClick={() => void refreshStudyActivity()}>Try again</s-button>
      </s-banner>
    )
  if (!firstUnreadRunId) return null

  const countLabel = formatShopifyStudyActivityBannerTitle(unreadCount)
  const body = formatShopifyStudyActivityBannerBody(unreadCount)

  return (
    <s-banner ref={bannerRef} heading={countLabel} tone="success" dismissible>
      <s-paragraph>{body}</s-paragraph>
      <s-button slot="secondary-actions" onClick={() => openReport(firstUnreadRunId)}>
        {formatShopifyStudyActivityBannerAction(unreadCount)}
      </s-button>
    </s-banner>
  )
}
